import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InvestorLayout from '../components/InvestorLayout';
import investorService from '../services/investorService';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { User, Mail, Phone, MapPin, Home, Building, LandPlot, Save, AlertTriangle, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const InvestorProfilePage = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Kişisel bilgiler
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');

  // Yatırımcı profili
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [interests, setInterests] = useState({
    residential: false,
    commercial: false,
    land: false,
  });
  const [preferredAreas, setPreferredAreas] = useState('');
  const [notes, setNotes] = useState('');

  // Şifre değiştirme
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const fetchProfile = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await investorService.getInvestorProfile(user.id);

      if (fetchError) throw fetchError;

      if (data) {
        // Kişisel bilgileri doldur
        const fullName = data.full_name || '';
        const nameParts = fullName.split(' ');
        setFirstName(nameParts[0] || '');
        setLastName(nameParts.slice(1).join(' ') || '');
        setEmail(data.email || user?.email || '');
        setPhone(data.phone || '');

        // Yatırımcı bilgileri
        if (data.investor) {
          setCountry(data.investor.country || '');
          setCity(data.investor.city || '');
          setAddress(''); // address alanı investor_profiles'da yoksa boş kalır
          setBudgetMin(data.investor.investment_budget?.toString() || '');
          setBudgetMax('');
          setPreferredAreas((data.investor.preferred_locations || []).join(', '));
          setNotes(data.investor.investment_goal || '');

          // İlgi alanları
          const types = data.investor.preferred_property_types || [];
          setInterests({
            residential: types.includes('residential'),
            commercial: types.includes('commercial'),
            land: types.includes('land'),
          });
        }
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
      setError(err.message || t('investor.dashboard.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.email, t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Form kaydetme
  const handleSave = async () => {
    if (!firstName.trim()) {
      toast({
        title: t('auth.required_field'),
        description: t('profile.firstName'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      setSuccessMessage('');

      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

      // İlgi alanları array'e çevir
      const preferredPropertyTypes = [];
      if (interests.residential) preferredPropertyTypes.push('residential');
      if (interests.commercial) preferredPropertyTypes.push('commercial');
      if (interests.land) preferredPropertyTypes.push('land');

      // Tercih edilen bölgeler array'e çevir
      const preferredLocations = preferredAreas
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const updateData = {
        full_name: fullName,
        phone: phone.trim(),
        country: country.trim(),
        city: city.trim(),
        investment_budget: budgetMin ? parseFloat(budgetMin) : null,
        preferred_property_types: preferredPropertyTypes,
        preferred_locations: preferredLocations,
        investment_goal: notes.trim() || null,
      };

      const { error: updateError } = await investorService.updateInvestorProfile(user.id, updateData);

      if (updateError) throw updateError;

      setSuccessMessage(t('profile.saveSuccess'));
      toast({
        title: t('profile.saveSuccess'),
        variant: 'default',
      });

      // Başarı mesajını 3 saniye sonra kaldır
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error('Profile save error:', err);
      toast({
        title: t('profile.saveError'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Hesap silme
  const handleDeleteAccount = async () => {
    try {
      // TODO: Backend'de hesap silme implementasyonu
      await logout();
    } catch (err) {
      console.error('Delete account error:', err);
      toast({
        title: 'Hata',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  // İlgi alanı toggle
  const handleInterestChange = (type) => {
    setInterests(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // İlgi alanı ikonları
  const interestIcons = {
    residential: Home,
    commercial: Building,
    land: LandPlot,
  };

  // Skeleton
  const FormSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  );

  if (error) {
    return (
      <InvestorLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <AlertCircle size={48} className="text-red-500 mb-4" />
          <h2 className="text-2xl font-heading text-[#0A192F] mb-2">
            {t('investor.dashboard.error_title')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            onClick={fetchProfile}
            className="bg-[#0A192F] text-white hover:bg-[#112240]"
          >
            <RefreshCw size={20} className="mr-2" />
            {t('investor.dashboard.retry')}
          </Button>
        </div>
      </InvestorLayout>
    );
  }

  return (
    <InvestorLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading text-[#0A192F] mb-2">
            {t('profile.title')}
          </h1>
          <p className="text-gray-600">
            {t('investor.dashboard.welcome')}
          </p>
        </div>

        {/* Başarı Mesajı */}
        {successMessage && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle size={16} className="text-green-600" />
            <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Kişisel Bilgiler */}
        <Card className="border border-gray-200">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <User size={20} className="text-[#0A192F]" />
              <CardTitle className="text-lg font-heading text-[#0A192F]">
                {t('profile.personalInfo')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <FormSkeleton />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Ad */}
                <div className="space-y-2">
                  <Label htmlFor="firstName">{t('profile.firstName')}</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('profile.firstName')}
                  />
                </div>

                {/* Soyad */}
                <div className="space-y-2">
                  <Label htmlFor="lastName">{t('profile.lastName')}</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('profile.lastName')}
                  />
                </div>

                {/* E-posta (read only) */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="email">
                    <div className="flex items-center space-x-2">
                      <Mail size={14} className="text-gray-500" />
                      <span>{t('profile.email')}</span>
                    </div>
                  </Label>
                  <Input
                    id="email"
                    value={email}
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500">{t('profile.emailReadOnly')}</p>
                </div>

                {/* Telefon */}
                <div className="space-y-2">
                  <Label htmlFor="phone">
                    <div className="flex items-center space-x-2">
                      <Phone size={14} className="text-gray-500" />
                      <span>{t('profile.phone')}</span>
                    </div>
                  </Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+90 5XX XXX XX XX"
                  />
                </div>

                {/* Ülke */}
                <div className="space-y-2">
                  <Label htmlFor="country">
                    <div className="flex items-center space-x-2">
                      <MapPin size={14} className="text-gray-500" />
                      <span>{t('profile.country')}</span>
                    </div>
                  </Label>
                  <Input
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder={t('profile.country')}
                  />
                </div>

                {/* Şehir */}
                <div className="space-y-2">
                  <Label htmlFor="city">{t('profile.city')}</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={t('profile.city')}
                  />
                </div>

                {/* Adres */}
                <div className="space-y-2">
                  <Label htmlFor="address">{t('profile.address')}</Label>
                  <Input
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t('profile.address')}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Yatırımcı Bilgileri */}
        <Card className="border border-gray-200">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Building size={20} className="text-[#0A192F]" />
              <CardTitle className="text-lg font-heading text-[#0A192F]">
                {t('profile.investorInfo')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <FormSkeleton />
            ) : (
              <div className="space-y-6">
                {/* Yatırım Bütçesi */}
                <div className="space-y-2">
                  <Label>{t('profile.investmentBudget')} (£)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <span className="text-xs text-gray-500">{t('profile.budgetFrom')}</span>
                      <Input
                        type="number"
                        value={budgetMin}
                        onChange={(e) => setBudgetMin(e.target.value)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-gray-500">{t('profile.budgetTo')}</span>
                      <Input
                        type="number"
                        value={budgetMax}
                        onChange={(e) => setBudgetMax(e.target.value)}
                        placeholder="1,000,000"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* İlgi Alanları */}
                <div className="space-y-2">
                  <Label>{t('profile.interests')}</Label>
                  <p className="text-sm text-gray-500">{t('profile.interestsDesc')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                    {Object.entries(interests).map(([key, checked]) => {
                      const Icon = interestIcons[key];
                      return (
                        <div
                          key={key}
                          className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            checked
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleInterestChange(key)}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => handleInterestChange(key)}
                            className="data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                          />
                          <Icon size={18} className={checked ? 'text-[#D4AF37]' : 'text-gray-500'} />
                          <span className={`text-sm font-medium ${checked ? 'text-[#0A192F]' : 'text-gray-600'}`}>
                            {t(`profile.propertyTypes.${key}`)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Tercih Edilen Bölgeler */}
                <div className="space-y-2">
                  <Label htmlFor="preferredAreas">{t('profile.preferredAreas')}</Label>
                  <p className="text-sm text-gray-500">{t('profile.preferredAreasDesc')}</p>
                  <Input
                    id="preferredAreas"
                    value={preferredAreas}
                    onChange={(e) => setPreferredAreas(e.target.value)}
                    placeholder="Girne, İskele, Lefkoşa"
                  />
                </div>

                {/* Notlar */}
                <div className="space-y-2">
                  <Label htmlFor="notes">{t('profile.notes')}</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('profile.notes')}
                    rows={4}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kaydet Butonu */}
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          className="w-full bg-[#D4AF37] text-white hover:bg-[#B5952F] disabled:opacity-50"
        >
          {saving ? (
            <>
              <RefreshCw size={16} className="mr-2 animate-spin" />
              Kaydediliyor...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              {t('profile.save')}
            </>
          )}
        </Button>

        {/* Şifre Değiştirme */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-[#0A192F]">
              {t('profile.changePassword')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!showPasswordChange ? (
              <Button
                variant="outline"
                onClick={() => setShowPasswordChange(true)}
                className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
              >
                {t('profile.changePassword')}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('auth.password')}</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('auth.password_confirm')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    className="bg-[#0A192F] text-white hover:bg-[#112240]"
                    disabled={!newPassword || newPassword !== confirmPassword || newPassword.length < 6}
                    onClick={() => {
                      // TODO: Şifre değiştirme implementasyonu
                      toast({
                        title: 'Şifre güncellendi',
                        variant: 'default',
                      });
                      setShowPasswordChange(false);
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    {t('profile.save')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    İptal
                  </Button>
                </div>
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <Alert variant="destructive" className="py-2">
                    <AlertCircle size={16} />
                    <AlertDescription className="text-sm">{t('auth.passwords_do_not_match')}</AlertDescription>
                  </Alert>
                )}
                {newPassword && newPassword.length > 0 && newPassword.length < 6 && (
                  <Alert variant="destructive" className="py-2">
                    <AlertCircle size={16} />
                    <AlertDescription className="text-sm">{t('auth.weak_password')}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hesabı Sil */}
        <Card className="border border-red-200 bg-red-50/30">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <AlertTriangle size={20} className="text-red-500" />
              <CardTitle className="text-lg font-heading text-red-700">
                {t('profile.deleteAccount')}
              </CardTitle>
            </div>
            <CardDescription className="text-red-600">
              {t('profile.deleteAccountWarning')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                >
                  <AlertTriangle size={16} className="mr-2" />
                  {t('profile.deleteAccount')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('profile.deleteAccount')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('profile.deleteAccountWarning')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>İptal</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    className="bg-red-500 text-white hover:bg-red-600"
                  >
                    {t('profile.deleteAccountConfirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </InvestorLayout>
  );
};

export default InvestorProfilePage;
